import {
  Box,
  Text,
  Group,
  Tooltip,
  Stack,
  useMantineColorScheme,
} from "@mantine/core";
import { useMemo } from "react";
import classes from "./HeatMap.module.scss";

const HeatMap = ({
  data = {},
  startDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
  view = "year",
  currentYear = new Date().getFullYear(),
}) => {
  const theme = useMantineColorScheme();

  // const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const getColor = (count = 0) => {
    if (theme.colorScheme === "light") {
      if (count === 0) return "var(--mantine-color-gray-2)";
      if (count <= 50) return "var(--mantine-color-teal-3)";
      if (count <= 100) return "var(--mantine-color-teal-5)";
      if (count <= 200) return "var(--mantine-color-teal-7)";
      return "var(--mantine-color-teal-9)";
    } else {
      if (count === 0) return "var(--mantine-color-dark-6)";
      if (count <= 50) return "var(--mantine-color-teal-9)";
      if (count <= 100) return "var(--mantine-color-teal-5)";
      if (count <= 200) return "var(--mantine-color-teal-3)";
      return "var(--mantine-color-teal-1)";
    }
  };

  // Generate calendar data based on view
  const calendarData = useMemo(() => {
    let endDate = new Date();
    const weeks = [];
    // let currentDate = new Date(startDate);
    let currentDate = new Date(currentYear, 0, 0);

    // For month view, adjust to current month
    if (view === "month") {
      currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      endDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    }
    // currentDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    // Adjust to start from the beginning of the week (Sunday)
    const dayOfWeek = currentDate.getDay();
    currentDate.setDate(currentDate.getDate() - dayOfWeek);

    // Calculate number of weeks to show
    const weekCount = view === "year" ? 52 : 6;

    // Generate data for each week
    for (let week = 0; week < weekCount; week++) {
      const weekData = [];
      for (let day = 0; day < 7; day++) {
        const date = new Date(currentDate);
        // This does a timezone conversion
        // const dateString = date.toISOString().split("T")[0];

        const dateString = new Date(
          date.getTime() - date.getTimezoneOffset() * 60000
        )
          .toISOString()
          .split("T")[0];

        // For month view, only include days in current month
        const shouldInclude =
          view === "year" ||
          (view === "month" && date.getMonth() === endDate.getMonth());

        weekData.push({
          date: date,
          count: shouldInclude ? data[dateString] || 0 : null,
          isCurrentMonth: shouldInclude,
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      weeks.push(weekData);
    }
    return weeks;
  }, [data, view, startDate, currentYear]);

  // Get month labels
  const monthLabels = useMemo(() => {
    if (view === "month") {
      // const currentDate = new Date();
      const currentDate = startDate;
      return [
        {
          month: currentDate.toLocaleString("default", {
            month: "long",
            year: "numeric",
          }),
          index: 0,
        },
      ];
    }

    const labels = [];
    const firstDates = calendarData.map((week) => week[0].date);
    let currentMonth = -1;

    firstDates.forEach((date, index) => {
      const month = date.getMonth();
      let currentDate = new Date(currentYear, 0);

      if (month !== currentMonth && date >= currentDate) {
        labels.push({
          month: date.toLocaleString("default", { month: "short" }),
          index,
        });
        currentMonth = month;
      }
    });
    return labels;
  }, [calendarData, view, currentYear]);

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <Box>
      <Stack gap="sm">
        {/* <Group justify="flex-end">
          
          <SegmentedControl
            value={view}
            onChange={setView}
            data={[
              { label: 'Year', value: 'year' },
              { label: 'Month', value: 'month' }
            ]}
          />
        </Group> */}

        {/* Month labels */}

        <Group align="start" gap={0} wrap="nowrap">
          {/* Day labels */}
          <Box
            w={56}
            pt={30}
            style={{
              flexShrink: 0,
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-evenly",
            }}
          >
            {days.map((day, i) => (
              <Text display={"block"} key={i} size="sm" h={16}>
                {day}
              </Text>
            ))}
          </Box>

          {/* Grid */}
          <Box style={{ width: "100%", overflow: "auto" }}>
            <Box
              style={{
                width: "100%",
                minWidth: view === "year" ? 940 : "100%",
              }}
            >
              <Box
                style={{
                  display: "flex",
                  position: "relative",
                  justifyContent: "space-evenly",
                }}
                mb={"xs"}
              >
                {monthLabels.map(({ month, index }, i) => (
                  <div
                    key={index}
                    style={{
                      width: view === "year" ? "100%" : "100%",
                      textAlign: "center",
                    }}
                  >
                    <Text
                      key={i}
                      size="sm"
                      style={{
                        position: view === "month" ? "relative" : "relative",
                        // left: view === 'month' ? 0 : `${(index * 18) + 56}px`
                      }}
                    >
                      {month}
                    </Text>
                  </div>
                ))}
              </Box>
              <Box style={{ flex: 1 }}>
                <Box
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${
                      view === "year" ? 52 : 6
                    }, 1fr)`,
                    gap: 2,
                  }}
                >
                  {calendarData.map((week, weekIndex) => (
                    <Box
                      key={weekIndex}
                      style={{
                        display: "grid",
                        gridTemplateRows: "repeat(7, 1fr)",
                        gap: 2,
                      }}
                    >
                      {week.map((day, dayIndex) => (
                        <Tooltip
                          key={`${weekIndex}-${dayIndex}`}
                          label={
                            day.isCurrentMonth
                              ? `${day.date.toLocaleDateString()}: ${
                                  day.count
                                } Distributions`
                              : null
                          }
                          position="top"
                          offset={5}
                          disabled={!day.isCurrentMonth}
                          styles={{
                            tooltip: {
                              backgroundColor: "var(--mantine-color-dark-9)",
                              color: "var(--mantine-color-white)",
                            },
                          }}
                        >
                          <Box
                            w={view === "month" ? "100%" : 16}
                            h={16}
                            className={classes.cell}
                            style={{
                              backgroundColor: day.isCurrentMonth
                                ? getColor(day.count)
                                : "auto",
                              transition: "background-color 100ms ease",
                              borderRadius: 3,
                            }}
                          ></Box>
                        </Tooltip>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Group>

        {/* Legend */}
        <Group gap={3} pl={56}>
          <Text size="sm" mr={"xs"}>
            Less
          </Text>
          {[0, 50, 100, 200, 300].map((count) => (
            <Box
              key={count}
              w={16}
              h={16}
              style={{ backgroundColor: getColor(count), borderRadius: 3 }}
            />
          ))}
          <Text ml={"xs"} size="sm">
            More
          </Text>
        </Group>
      </Stack>
    </Box>
  );
};

export default HeatMap;
